import React, { createContext, useEffect, useRef, useState } from "react";
import { connect } from 'react-redux';
import { compose } from 'redux';
import Prompt from '../components/prompt/'
import * as Actions from '../redux/actions/';
import { auth, generateUserDocument } from "../firebase";

export const UserContext = createContext({ user: null });

function UserProvider({addNotification, children, setIsAuthenticating }) {
	const [user, setUser] = useState(null);
	const [showPrompt, setShowPrompt] = useState(false);
	const sessionTimeout = useRef(null);
	const confirmationTimeout = useRef(null);
	const SESSION_DURATION = 3600000;

	function startSessionTimer(duration) {
		sessionTimeout.current = setTimeout(handleSessionTimeout, duration);
	}
	function clearTimers() {
		clearTimeout(confirmationTimeout.current);
		clearTimeout(sessionTimeout.current);
		sessionTimeout.current = null;
		confirmationTimeout.current = null;
	}
	function handleSessionTimeout() {
		setShowPrompt(true);
	}
	const handleSessionDuration = (idTokenResult, authUser, callback) => {
		const authTime = idTokenResult.claims.auth_time * 1000;
		const msUntilExpiration = SESSION_DURATION - (Date.now() - authTime);
		startSessionTimer(msUntilExpiration);

		callback(authUser);
	}

	function onAuthStateChange(callback) {
		return auth.onAuthStateChanged(async authUser => {
			if (authUser === null) {
				// User is logged out. Clear the session timeout.
				clearTimers();
				callback(null);
			} else {
				// User is logged in. Start Auto logout timer
				const idToken = await authUser.getIdTokenResult();
				handleSessionDuration(idToken, authUser, callback);
			}
		});
	}
	useEffect(() => {
    	const unsubscribe = onAuthStateChange(handleAuthStateChange);
	    return () => {
	      unsubscribe();
	    }
	}, []);

	async function handleAuthStateChange (authData) {
		if (!authData) {
			setUser(null);
			return;
		}
		const mergeWithUserData = async () => {
			setIsAuthenticating(true);
			// This ugly hack gets around firebase auth weirdness where displayName is sometimes null even when it's clearly there on the currentUser Object
			if (authData.displayName === null){
				setTimeout(() => {
					mergeWithUserData()
				}, 500);
			} else {
				try {
					const userData = await generateUserDocument(authData);
					setUser(userData);
					setIsAuthenticating(false);
				} catch (error) {
					addNotification({type: 'error', message: 'Unable to generate user profile.'});
					setIsAuthenticating(false);
				}
			}
		}
		mergeWithUserData();
	}

	return (
    <UserContext.Provider value={user}>
      <>
	  	{showPrompt && (
			<Prompt
				autoClose={90000}
				onAutoClose={() => {
					setShowPrompt(false);
					auth.signOut();
				}}
				content="Your session will auto expire in {{timer}} seconds. If you take no action, you will be logged out and lose any unsaved work."
				onPrimaryAction={() => {
					setShowPrompt(false);
					clearTimers();
					startSessionTimer(SESSION_DURATION);
				}}
				onSecondaryAction={() => {
					setShowPrompt(false);
					clearTimers();
					auth.signOut();
				}}
				primaryAction="Stay Logged In"
				secondaryAction="Logout"
			 />
		)}
	  	{children}
	  </>
    </UserContext.Provider>
  );
}

const mapStateToProps = state => {
  return {
	isAuthenticating: state.isAuthenticating
  }
}

const mapDispatchToProps = {
	addNotification: Actions.addNotification,
	setIsAuthenticating: Actions.setIsAuthenticating
};

export default compose(
	connect(mapStateToProps, mapDispatchToProps)
)(UserProvider);
